import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { isEmpty } from "lodash-es";
import { defineMessages, useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import {
  Deal,
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
  Resource,
} from "@mapmycustomers/shared/types/entity";
import { isNotEmptyString } from "@mapmycustomers/shared/util/assert";
import parseEmailDynamicVariables from "@mapmycustomers/shared/util/email/parseEmailDynamicVariables";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { EmailAttachedFile } from "@mapmycustomers/ui";

import { getEmailDraftsAttachmentIds, getEmailTemplatesAttachmentIds } from "@app/store/email";
import {
  deleteEmailAttachedFile,
  deleteEmailDraft,
  downloadEmailAttachedFile,
  fetchNewEmailPreview,
  uploadFileForNewEmail,
  uploadImageForNewEmail,
} from "@app/store/email/actions";
import { getBulkEmailSignatureText, getOrganizationEmailBoilerplateText } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import EmailTemplateInfo from "@app/types/email/EmailTemplateInfo";
import appendEmailBoilerplate from "@app/util/email/appendEmailBoilerplate";
import appendEmailSignature from "@app/util/email/appendEmailSignature";
import useEmailDraftInfoGetter from "@app/util/email/useEmailDraftInfoGetter";
import useEmailObjectsListeners, {
  UseEmailObjectsListenersArgs,
} from "@app/util/email/useEmailObjectsListeners";
import useEmailResources from "@app/util/email/useEmailResources";
import useEmailTemplateInfoGetter from "@app/util/email/useEmailTemplateInfoGetter";
import useEntityTypeEmailDrafts from "@app/util/email/useEntityTypeEmailDrafts";
import getEmailDynamicVariablesInfo from "@app/util/fieldModel/util/getEmailDynamicVariablesInfo";

import EmailContext, { EmailContextValue, EmailContextValueState } from "./EmailContext";

const messages = defineMessages({
  emailBodyLimitReached: {
    id: "email.creation.emailBodyLimitReached",
    defaultMessage: "The email body is too long. Please shorten it to proceed.",
    description: "Email body limit reached",
  },
  filesSizeLimitReached: {
    id: "email.creation.filesSizeLimitReached",
    defaultMessage:
      "Cannot send emails with attachments larger than 24 MB in total. Please remove or compress files and try again.",
    description: "Email attachments limit reached",
  },
});

const BODY_MAX_LENGTH = 384000;

const ATTACHED_FILES_SIZE_LIMIT = 24 * 1024 * 1024;

interface EmailProviderProps {
  children: React.ReactNode;
  initialValues?: {
    // Seeds the editor with a body the caller already has — the Field Agent's drafted email. Without
    // it the only seed is defaultBody, which is the signature alone.
    body?: string;
    deals?: Deal[];
    draftEnabled?: boolean;
    dynamicVariablesSupported?: boolean;
    entities?: EntitiesSupportedByEmailFeature[];
    entityType?: EntityTypesSupportedByEmailFeature;
    subject?: string;
    templateInfo?: EmailTemplateInfo;
  };
}

const EmailProvider = ({
  boilerplateText,
  children,
  draftsAttachmentIds,
  initialValues,
  onFileDelete,
  onFileUpload,
  onImageUpload,
  onPreviewFetch,
  signatureText,
  templatesAttachmentIds,
}: EmailProviderProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const defaultBody = useMemo(() => appendEmailSignature("", signatureText), [signatureText]);

  const [imageUploading, setImageUploading] = useState<boolean>(false);

  const [state, setState] = useState<EmailContextValueState>(() => {
    const { body, deals, dynamicVariablesSupported, entities, entityType, subject, templateInfo } =
      initialValues ?? {};
    const filteredRecipients = (entities ?? []).filter(({ email }) => !isEmpty(email));
    return {
      aiWriterVisible: false,
      associatedEntities: [],
      attachedFiles:
        templateInfo?.template.attachments?.map((file) => ({ id: uuidv4(), file })) ?? [],
      attachedFilesTouched: false,
      // An explicit body wins over both: a caller that hands one over has already composed it.
      body: body ?? (templateInfo ? templateInfo.template.body : defaultBody),
      bodyTouched: false,
      deals: deals ?? [],
      draftEnabled: initialValues?.draftEnabled ?? true,
      draftSaving: false,
      dynamicVariablesSupported: dynamicVariablesSupported ?? true,
      entities: filteredRecipients,
      entityType:
        entities?.[0]?.entity ??
        templateInfo?.template?.entityType ??
        entityType ??
        EntityType.COMPANY,
      previewLoading: false,
      previewOpen: false,
      sending: false,
      sent: false,
      subject: subject ?? templateInfo?.template?.subject ?? "",
      subjectTouched: false,
      template: templateInfo?.template,
      templateChanged: false,
    };
  });

  const {
    onResourceDownload,
    onResourceLinkInsert,
    onResourceLinksChange,
    onResourcesChange,
    renderResourcesModals,
    resourceGetter,
    resources,
  } = useEmailResources({ editor: state.editor });

  const attachedFilesSizeLimitReached = useMemo(() => {
    return (
      ATTACHED_FILES_SIZE_LIMIT <
      state.attachedFiles.reduce<number>(
        (result, attachment) =>
          result + (attachment.file?.contentLength ?? attachment.localFile!.size),
        0
      )
    );
  }, [state.attachedFiles]);

  const updateState = useCallback((newState: Partial<EmailContextValueState>) => {
    setState((state) => ({ ...state, ...newState }));
  }, []);

  const emailFilled = useMemo(
    () => isNotEmptyString(state.body) && isNotEmptyString(state.subject),
    [state.body, state.subject]
  );

  const bodyLimitReached = state.body.length >= BODY_MAX_LENGTH;
  const emailTouched = state.bodyTouched || state.subjectTouched || state.attachedFilesTouched;

  const subjectParsedDynamicVariables = useMemo(
    () => (state.dynamicVariablesSupported ? parseEmailDynamicVariables(state.subject) : []),
    [state.dynamicVariablesSupported, state.subject]
  );

  const bodyParsedDynamicVariables = useMemo(
    () => (state.dynamicVariablesSupported ? parseEmailDynamicVariables(state.body) : []),
    [state.dynamicVariablesSupported, state.body]
  );

  const [dynamicVariables, entitiesMissedFields] = useMemo(() => {
    return state.dynamicVariablesSupported
      ? getEmailDynamicVariablesInfo({
          entities: state.entities,
          entityType: state.entityType,
          intl,
          parsedDynamicVariables: [...subjectParsedDynamicVariables, ...bodyParsedDynamicVariables],
        })
      : [[], {}];
  }, [
    state.dynamicVariablesSupported,
    state.entities,
    state.entityType,
    intl,
    subjectParsedDynamicVariables,
    bodyParsedDynamicVariables,
  ]);

  const composedBody = useMemo(() => {
    let result = appendEmailBoilerplate(state.body, boilerplateText);

    if (state.unsubscribeLinkIncluded) {
      result += `
        <div>          
          <div>--</div>
          <a href="{emailUnsubscribeLink}">${intl.formatMessage({
            id: "email.creation.body.unsubscribeLink",
            defaultMessage: "Unsubscribe",
            description: "Unsubscribe link",
          })}</a>
        </div>
      `;
    }

    return result;
  }, [intl, state.body, state.unsubscribeLinkIncluded, boilerplateText]);

  const subjectContainsDynamicVariables = !!subjectParsedDynamicVariables.length;
  const bodyContainsDynamicVariables = !!bodyParsedDynamicVariables.length;

  const validationError = useMemo(() => {
    if (attachedFilesSizeLimitReached) {
      return intl.formatMessage(messages.filesSizeLimitReached);
    }

    if (bodyLimitReached) {
      return intl.formatMessage(messages.emailBodyLimitReached);
    }
  }, [intl, attachedFilesSizeLimitReached, bodyLimitReached]);

  const drafts = useEntityTypeEmailDrafts(state.entityType);

  const draftInfoGetter = useEmailDraftInfoGetter();
  const templateInfoGetter = useEmailTemplateInfoGetter();

  const handleFileAttach = useDynamicCallback<EmailContextValue["onFileAttach"]>((file) => {
    const attachingFile: EmailAttachedFile = {
      id: uuidv4(),
      localFile: file,
    };
    setState((state) => ({
      ...state,
      attachedFiles: [...state.attachedFiles, { ...attachingFile, uploading: true }],
    }));
    onFileUpload({
      attachedFile: attachingFile,
      callback: (uploadedFile) => {
        setState((state) => ({
          ...state,
          attachedFiles: state.attachedFiles.map((attachment) => {
            if (uploadedFile.id === attachment.id) {
              return { ...uploadedFile, uploading: false };
            }
            return attachment;
          }),
          attachedFilesTouched: true,
          templateChanged: !!state.template,
        }));
      },
      failureCallback: () =>
        setState((state) => ({
          ...state,
          attachedFiles: state.attachedFiles.map((attachedFile) => {
            if (attachingFile.id === attachedFile.id) {
              return { ...attachedFile, errored: true, uploading: false };
            }
            return attachedFile;
          }),
        })),
    });
  });

  const handleAttachedFileDelete = useDynamicCallback<EmailContextValue["onAttachedFileDelete"]>(
    (attachedFile) => {
      setState((state) => ({
        ...state,
        attachedFiles: state.attachedFiles.filter(({ id }) => id !== attachedFile.id),
        attachedFilesTouched: true,
        templateChanged: !!state.template,
      }));

      if (
        attachedFile.file &&
        !draftsAttachmentIds.includes(attachedFile.file.id) &&
        !templatesAttachmentIds.includes(attachedFile.file.id)
      ) {
        onFileDelete(attachedFile.file.id);
      }
    }
  );

  const handlePreviewCreate = useDynamicCallback<EmailContextValue["onPreviewCreate"]>((email) => {
    updateState({ preview: undefined, previewLoading: true });
    onPreviewFetch({
      callback: (emailPreview) => updateState({ preview: emailPreview, previewLoading: false }),
      email,
      failureCallback: () => updateState({ previewLoading: false }),
    });
  });

  const handleSubjectChange = useDynamicCallback<EmailContextValue["onSubjectChange"]>(
    (subject) => {
      updateState({ subject, subjectTouched: true, templateChanged: !!state.template });
    }
  );

  const handleEntitiesChange = useDynamicCallback<EmailContextValue["onEntitiesChange"]>(
    (recipients) => {
      if (recipients.some((recipient) => recipient.entity !== state.entityType)) {
        throw new Error("Recipients are not matching with recipient type");
      }

      updateState({ entities: recipients });
    }
  );

  const handleEntityTypeChange = useDynamicCallback<EmailContextValue["onEntityTypeChange"]>(
    (recipientType) => {
      updateState({ entityType: recipientType });
    }
  );

  const handleTemplateChange = useDynamicCallback<EmailContextValue["onTemplateChange"]>(
    (template) => {
      let resources: Resource[];

      if (template) {
        const templateInfo = templateInfoGetter(template.id)!;
        resources = templateInfo.resources;
        updateState({
          aiSubjectSuggestion: undefined,
          attachedFiles: (templateInfo.template.attachments ?? []).map((attachment) => ({
            id: uuidv4(),
            file: attachment,
          })),
          body: template.body,
          subject: template.subject,
          template,
          templateChanged: false,
        });
      } else {
        resources = [];
        updateState({
          aiSubjectSuggestion: undefined,
          attachedFiles: [],
          body: defaultBody,
          subject: "",
          template: undefined,
          templateChanged: false,
        });
      }

      onResourcesChange(resources);
    }
  );

  const handleDraftChange = useDynamicCallback<EmailContextValue["onDraftChange"]>((draftId) => {
    if (draftId) {
      const { draft, entities, resources } = draftInfoGetter(draftId, intl)!;

      updateState({
        aiSubjectSuggestion: undefined,
        attachedFiles: draft.attachments.map((attachment) => ({
          id: uuidv4(),
          file: attachment,
        })),
        attachedFilesTouched: false,
        body: draft.content,
        bodyTouched: false,
        draftId,
        entities: entities.filter(({ email }) => !!email),
        subject: draft.title,
        subjectTouched: false,
        templateChanged: !!state.template,
      });

      onResourcesChange(resources);
    } else {
      updateState({ draftId: undefined });
    }
  });

  const handleDraftCreated = useDynamicCallback<EmailContextValue["onDraftCreated"]>((draftId) => {
    updateState({
      draftId,
    });
  });

  const handleUnsubscribeLinkIncludedChange = useDynamicCallback<
    EmailContextValue["onUnsubscribeLinkIncludedChange"]
  >((unsubscribeLinkIncluded: boolean) => {
    updateState({ unsubscribeLinkIncluded });
  });

  const handlePreviewShow = useDynamicCallback<EmailContextValue["onPreviewShow"]>(() => {
    updateState({ previewOpen: true });
  });

  const handlePreviewHide = useDynamicCallback<EmailContextValue["onPreviewHide"]>(() => {
    updateState({ previewOpen: false });
  });

  const handlePreviewRecipientIdChange = useDynamicCallback<
    EmailContextValue["onPreviewRecipientIdChange"]
  >((previewRecipientId) => {
    updateState({ previewRecipientId });
  });

  const handleInit = useDynamicCallback<EmailContextValue["onInit"]>((editor) => {
    updateState({ editor });
  });

  const handleChange = useDynamicCallback<EmailContextValue["onChange"]>((text: string) => {
    setState((prev) => ({
      ...prev,
      aiSubjectSuggestion: text === prev.body ? prev.aiSubjectSuggestion : undefined,
      body: text,
      bodyTouched: true,
      templateChanged: !!prev.template,
    }));
  });

  const handleAiSubjectSuggestionChange = useDynamicCallback<
    EmailContextValue["onAiSubjectSuggestionChange"]
  >((aiSubjectSuggestion) => {
    updateState({ aiSubjectSuggestion });
  });

  const handleSendingChange = useDynamicCallback<EmailContextValue["onSendingChange"]>((value) => {
    updateState({ sending: value });
  });

  const handleDraftSavingChange = useDynamicCallback<EmailContextValue["onDraftSavingChange"]>(
    (value) => {
      updateState({ draftSaving: value });
    }
  );

  const handleImageUpload = useDynamicCallback<EmailContextValue["onImageUpload"]>(
    (file, callback) => {
      setImageUploading(true);
      onImageUpload({
        callback: (url) => {
          callback(url);
          setImageUploading(false);
        },
        file,
      });
    }
  );

  const handleSent = useDynamicCallback<EmailContextValue["onSent"]>(() => {
    updateState({ sent: true });
  });

  useEffect(() => {
    if (initialValues?.templateInfo) {
      onResourcesChange(initialValues.templateInfo.resources);
    }
    // it should run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.editor && state.template) {
      const templateInfo = templateInfoGetter(state.template.id);
      // selected template deleted
      if (!templateInfo) {
        updateState({ template: undefined, templateChanged: false });
      } else if (templateInfo.invalidResourceIds.length) {
        // according to requirements template with invalid resources can be used
        // invalid resource links should be removed from the body
        state.editor.removeResourceLinks(templateInfo.invalidResourceIds);
        onResourceLinksChange(state.editor.getResourceIds());
        updateState({ templateChanged: true });
      }
    }
  }, [state.editor, state.template, updateState, templateInfoGetter, onResourceLinksChange]);

  // remove resource links when resource deleted
  useEffect(() => {
    if (state.editor) {
      const resourceIds = state.editor.getResourceIds();
      const resourceIdsToRemove = resourceIds.filter((resourceId) => !resources.has(resourceId));
      if (resourceIdsToRemove.length) {
        state.editor.removeResourceLinks(resourceIdsToRemove);
      }
    }
  }, [resources, state.editor]);

  useEffect(() => {
    if (
      state.previewRecipientId &&
      !state.entities.some(({ id }) => id === state.previewRecipientId)
    ) {
      updateState({ previewRecipientId: undefined });
    }
  }, [state.entities, state.previewRecipientId, updateState]);

  useEffect(() => {
    if (state.draftId) {
      // selected draft deleted
      if (!drafts.some((draft) => draft.id === state.draftId)) {
        updateState({
          attachedFilesTouched: false,
          bodyTouched: false,
          draftId: undefined,
          subjectTouched: false,
        });
      }
    }
  }, [drafts, state.draftId, updateState]);

  useEmailObjectsListeners({
    entities: state.entities,
    onEntitiesChange: useDynamicCallback<UseEmailObjectsListenersArgs["onEntitiesChange"]>(
      (entities) => {
        updateState({ entities });
      }
    ),
    onResourcesChange: useDynamicCallback<UseEmailObjectsListenersArgs["onResourcesChange"]>(
      (resources) => {
        onResourcesChange(resources);
      }
    ),
    resources,
  });

  const handleAiWriterToggle = useDynamicCallback<EmailContextValue["onAiWriterToggle"]>(() => {
    updateState({ aiWriterVisible: !state.aiWriterVisible });
  });

  return (
    <EmailContext.Provider
      value={{
        ...state,
        attachedFilesSizeLimitReached,
        bodyContainsDynamicVariables,
        bodyLimitReached,
        composedBody,
        dynamicVariables,
        emailFilled,
        emailTouched,
        entitiesMissedFields,
        loading: imageUploading,
        onAiSubjectSuggestionChange: handleAiSubjectSuggestionChange,
        onAiWriterToggle: handleAiWriterToggle,
        onAttachedFileDelete: handleAttachedFileDelete,
        onChange: handleChange,
        onDraftChange: handleDraftChange,
        onDraftCreated: handleDraftCreated,
        onDraftSavingChange: handleDraftSavingChange,
        onEntitiesChange: handleEntitiesChange,
        onEntityTypeChange: handleEntityTypeChange,
        onFileAttach: handleFileAttach,
        onImageUpload: handleImageUpload,
        onInit: handleInit,
        onPreviewCreate: handlePreviewCreate,
        onPreviewHide: handlePreviewHide,
        onPreviewRecipientIdChange: handlePreviewRecipientIdChange,
        onPreviewShow: handlePreviewShow,
        onResourceDownload: onResourceDownload,
        onResourceLinkInsert: onResourceLinkInsert,
        onResourceLinksChange: onResourceLinksChange,
        onSendingChange: handleSendingChange,
        onSent: handleSent,
        onSubjectChange: handleSubjectChange,
        onTemplateChange: handleTemplateChange,
        onUnsubscribeLinkIncludedChange: handleUnsubscribeLinkIncludedChange,
        resourceGetter,
        resources,
        subjectContainsDynamicVariables,
        validationError,
      }}
    >
      {children}
      {renderResourcesModals()}
    </EmailContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    boilerplateText: getOrganizationEmailBoilerplateText(state),
    draftsAttachmentIds: getEmailDraftsAttachmentIds(state),
    signatureText: getBulkEmailSignatureText(state),
    templatesAttachmentIds: getEmailTemplatesAttachmentIds(state),
  };
};

const mapDispatchToProps = {
  onDraftDelete: deleteEmailDraft.request,
  onFileDelete: deleteEmailAttachedFile,
  onFileDownload: downloadEmailAttachedFile,
  onFileUpload: uploadFileForNewEmail,
  onImageUpload: uploadImageForNewEmail,
  onPreviewFetch: fetchNewEmailPreview,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailProvider);
